import { SelectInput } from "../../../components/SelectInput/SelectInput";
import { Field } from "./CertificationFormModal";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import { useApp } from "../../../services/app-context";
import { getLabel } from "../utils/labels";
import { isCertificationOverallSummaryEnabled } from "../../../integrations/posthog/featureFlags";
import { twMerge } from "tailwind-merge";

const generateAttemptList = (minAttemptCount: number, maxAttemptCount: number) => {
  return Array.from({ length: maxAttemptCount - minAttemptCount + 1 }, (_, index) => ({ label: `${index + minAttemptCount}`, value: index + minAttemptCount }));
}

const fieldDisabledClass = "disabled:cursor-not-allowed disabled:!text-gray-402 disabled:!border-gray4 disabled:!bg-white [&_path]:stroke-gray-402";

function SkeletonBlock({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} aria-hidden />;
}

/** Shown while edit-mode certification + call-flow details are loading. */
function CertificationFormBasicFieldsSkeleton({ showAttemptRange }: { showAttemptRange: boolean }) {
  return (
    <div
      className="flex flex-col gap-6 border border-lightGray rounded-[16px] p-5 bg-gray-100 h-[75vh]"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading certification details"
    >
      <div className="flex gap-4 items-start flex-wrap">
        <div className="flex flex-col gap-1 min-w-[7rem] flex-1">
          <SkeletonBlock className="h-3.5 w-16" />
          <SkeletonBlock className="h-[33px] w-full max-w-[8rem]" />
        </div>
        <div className="flex flex-col gap-1 min-w-[7rem] flex-1">
          <SkeletonBlock className="h-3.5 w-14" />
          <SkeletonBlock className="h-[33px] w-full max-w-[8rem]" />
        </div>
        {showAttemptRange && (
          <div className="flex flex-col gap-1 min-w-[5rem]">
            <SkeletonBlock className="h-3.5 w-8" />
            <SkeletonBlock className="h-[33px] w-[100px]" />
          </div>
        )}
        <div className="flex flex-col gap-1 min-w-[5rem]">
          <SkeletonBlock className="h-3.5 w-8" />
          <SkeletonBlock className="h-[33px] w-[100px]" />
        </div>
      </div>
      <div className="flex gap-4 items-start">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <SkeletonBlock className="h-3.5 w-28" />
          <SkeletonBlock className="h-[33px] w-full" />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0 w-[48%]">
          <SkeletonBlock className="h-3.5 w-24" />
          <SkeletonBlock className="h-[33px] w-full" />
        </div>
      </div>
      <div className="flex gap-4 items-start">
        <div className="flex flex-col gap-1 flex-1 min-w-0 w-[48%]">
          <SkeletonBlock className="h-3.5 w-32" />
          <SkeletonBlock className="h-[33px] w-full" />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <SkeletonBlock className="h-3.5 w-24" />
          <SkeletonBlock className="h-[33px] w-full" />
        </div>
      </div>
      <div className="flex gap-4 flex-1 min-h-0">
        <div className="flex flex-col gap-1 flex-1">
          <SkeletonBlock className="h-3.5 w-36" />
          <SkeletonBlock className="h-[72px] w-full" />
        </div>
      </div>
    </div>
  );
}

export function CertificationFormBasicFields({
  form,
  setForm,
  salesAidFiles,
  salesAidFileOptions,
  isEdit,
  isEditDataLoading = false,
  restrictToDueDateOnly = false,
  fullyLocked = false,
}: {
  form: any;
  setForm: (form: any) => void;
  salesAidFiles: any[];
  salesAidFileOptions: any[];
  isEdit?: boolean;
  /** When true, show skeleton (edit mode while details + call flow queries are in flight). */
  isEditDataLoading?: boolean;
  /** When true (edit + start date today or past), only due date is editable. */
  restrictToDueDateOnly?: boolean;
  /** When true (edit + start and due both before today), every field including due date is read-only. */
  fullyLocked?: boolean;
}) {
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const showOverallSummary = isCertificationOverallSummaryEnabled();
  const lockMetadata = fullyLocked || restrictToDueDateOnly;
  const dueDateDisabled = fullyLocked;

  if (isEdit && isEditDataLoading) {
    return <CertificationFormBasicFieldsSkeleton showAttemptRange={showOverallSummary} />;
  }

  return (
    <div className="flex flex-col gap-6 border border-lightGray rounded-[16px] p-5 bg-gray-100 h-[75vh]">
      <div className="flex gap-4 items-center justify-start">
        <Field label="Start Date" className="flex-1">
          <div className="min-h-[34px] flex items-center [&_.react-datepicker__input-container]:!block [&>div]:w-full">
            <DatePicker
              className={`border border-[#E9E9E9] w-full h-[33px] rounded text-xs px-2 !pl-3 py-1 !outline-none ${fieldDisabledClass}`}
              selected={form.certificationStartDate}
              onChange={(date) => {
                setForm((prev) => ({
                  ...prev,
                  certificationStartDate: date,
                  certificationDueDate: prev.certificationDueDate
                    ? date && dayjs(prev.certificationDueDate).isBefore(date)
                      ? null
                      : prev.certificationDueDate
                    : prev.certificationDueDate,
                }));
              }}
              dateFormat="MMM dd, yyyy"
              icon={<CalendarIcon className={twMerge(" !w-4 !h-4", lockMetadata ? "[&_path]:stroke-gray-402" : "[&_path]:stroke-[#000000]")} />}
              showIcon={true}
              toggleCalendarOnIconClick={!lockMetadata}
              calendarIconClassName="right-1"
              minDate={dayjs().toDate()}
              disabled={lockMetadata}
              withPortal
            />
          </div>
        </Field>
        <Field label="Due Date" className="flex-1">
          <div className="min-h-[34px] flex items-center [&_.react-datepicker__input-container]:!block [&>div]:w-full">
            <DatePicker
              className={`border border-[#E9E9E9] w-full h-[33px] rounded text-xs px-2 py-1 !pl-3 !outline-none ${dueDateDisabled ? fieldDisabledClass : ""}`}
              selected={form.certificationDueDate}
              onChange={(date) => setForm((prev) => ({ ...prev, certificationDueDate: date }))}
              dateFormat="MMM dd, yyyy"
              minDate={restrictToDueDateOnly ? dayjs().toDate() : form.certificationStartDate}
              icon={<CalendarIcon className={twMerge(" !w-4 !h-4", dueDateDisabled ? "[&_path]:stroke-gray-402" : "[&_path]:stroke-[#000000]")} />}
              showIcon={true}
              toggleCalendarOnIconClick={!dueDateDisabled}
              calendarIconClassName="right-1"
              disabled={dueDateDisabled}
              withPortal
            />
          </div>
        </Field>

        {showOverallSummary && <Field label={`Min. ${getLabel("attempt.plural", labels)}`} className="min-w-[100px]">
          <SelectInput
            className="border w-[100px] h-[33px] border-[#E9E9E9] text-xs rounded px-2 py-1 !outline-none"
            value={form.certificationAttemptsRequired}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                certificationAttemptsRequired: Number(e.target.value),
                certificationAttemptsAllowed: Number(e.target.value) > prev.certificationAttemptsAllowed ? "" : prev.certificationAttemptsAllowed,
              }));
            }}
            options={generateAttemptList(1, 3)}
            placeholder="Select"
            containerClassName="w-[90px]"
            disabled={lockMetadata}
          />
        </Field>}
        <Field label={`Max. ${getLabel("attempt.plural", labels)}`} className="min-w-[100px]">
          <SelectInput
            className="border w-[100px] h-[33px] border-[#E9E9E9] text-xs rounded px-2 py-1 !outline-none"
            value={form.certificationAttemptsAllowed}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                certificationAttemptsAllowed: Number(e.target.value),
              }));
            }}
            options={generateAttemptList(Number(form.certificationAttemptsRequired) || 1, 3)}
            placeholder="Select"
            containerClassName="w-[90px]"
            disabled={lockMetadata}
          />
        </Field>
      </div>
      <div className="flex gap-4 items-start">
        <Field label={`${getLabel("session.singular", labels)} Name`} className="flex-1">
          <input
            type="text"
            className={`border border-[#E9E9E9] h-[33px] text-xs rounded px-2 py-1 !outline-none ${fieldDisabledClass}`}
            value={form.certificationName}
            disabled={lockMetadata}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                certificationName: e.target.value,
              }));
            }}
          />
        </Field>

        <Field label="Product Name" className="flex-1 w-[48%]">
          <input
            type="text"
            className="border border-[#E9E9E9] h-[33px] text-xs rounded px-2 py-1 !outline-none"
            value={form.productName}
            readOnly
          />
        </Field>
      </div>

      <div className="flex gap-4 items-start">


        <Field label={`${getLabel("salesAidDocument", labels)}`} className="flex-1 w-[48%]">
          <SelectInput
            options={salesAidFileOptions}
            value={form.salesAidFile}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                salesAidFile: e.target.value,
                code: salesAidFiles.find((file) => file.id == e.target.value)?.code || "",
                callFlow: prev.callFlow?.map((item: any) => ({ ...item, category: "" })),
              }));
            }}
            placeholder="Select Document"
            className="h-[33px] text-xs"
            disabled={lockMetadata}
          />
        </Field>

        <Field label="Document Code" className="flex-1">
          <input
            type="text"
            className="border border-[#E9E9E9] h-[33px] text-xs rounded px-2 py-1 !outline-none"
            value={form.code}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                code: e.target.value,
              }));
            }}
            readOnly
          />
        </Field>

      </div>

      <div className="flex gap-4 flex-1">
        <Field label={`${getLabel("certification.singular", labels)} Intro Text`} className="flex-1">
          <textarea
            className={`border border-[#E9E9E9] text-xs rounded px-2 py-1 !outline-none flex-1 ${fieldDisabledClass}`}
            value={form.primer}
            rows={3}
            placeholder={getLabel("defaultPrimer", labels)}
            disabled={lockMetadata}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                primer: e.target.value,
              }));
            }}
          />
        </Field>

      </div>
    </div>
  );
}